import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  PartnerListExportToFileStart,
  PartnerListInitialState,
  PartnerListLoad,
  PartnerListLoadByFilter,
} from './partner-list.actions';
import { LIST_SERVER_PAGINATION_INITIAL_STATE, ListServerPaginationStateModel } from '../../../../../common/models';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { PartnerListService } from './partner-list.service';
import { PartnerBrief, PartnerListSearchCriteria, PartnerSearchCriteria } from '../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { NavigateUserService } from '../../../../services/navigate-user.service';

export type PartnerListStateModel = ListServerPaginationStateModel<PartnerBrief>;

const initialState: PartnerListStateModel = LIST_SERVER_PAGINATION_INITIAL_STATE;

@State<PartnerListStateModel>({
  name: 'partnerList',
  defaults: initialState,
})
@Injectable()
export class PartnerListState {
  constructor(
    private partnerListService: PartnerListService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getState(state: PartnerListStateModel): PartnerListStateModel {
    return state;
  }

  @Selector()
  static getItems(state: PartnerListStateModel): PartnerBrief[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: PartnerListStateModel): boolean {
    return state.loaded;
  }

  @Action(PartnerListLoad)
  getPartners(
    ctx: StateContext<PartnerListStateModel>,
    { payload }: PartnerListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.partnerListService.getPartnerList(payload as PartnerSearchCriteria).pipe(
      take(1),
      map((list: PartnerListStateModel) => {
        ctx.patchState({ ...list, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PartnerListInitialState)
  setInitialState(ctx: StateContext<PartnerListStateModel>): void {
    ctx.setState(initialState);
  }

  @Action(PartnerListExportToFileStart)
  startExportToFile(
    ctx: StateContext<PartnerListStateModel>,
    { payload: { dialogRef, navigationNeeded, ...body } }: PartnerListExportToFileStart,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.partnerListService.startExportToFile(body).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.export.success.exportStart');
        dialogRef.close();
        if (navigationNeeded) {
          this.navigateUserService.navigateUserToAbsolutePath('/dashboard/exports');
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PartnerListLoadByFilter)
  getPartnerListByFilter(
    ctx: StateContext<PartnerListStateModel>,
    { payload }: PartnerListLoadByFilter,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.partnerListService.getPartnerListByFilter(payload as PartnerListSearchCriteria).pipe(
      take(1),
      map((list: PartnerListStateModel) => {
        ctx.patchState({ ...list, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }
}
